import React, { useMemo } from 'react'
import { BarChart } from '@mui/x-charts'
import { Theme, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { IssueType } from '@finance-lab/utils/types'
import { getIssueTypeColors } from '../../../components/entities/IssueTypeLabels'
import {
    ChartDisplayRow,
    IssueTypeSegment,
    OFFER_SIZE_SEGMENTS,
    SegmentationType,
} from './utils'
import { BarSeries } from '@mui/x-charts/BarChart'

const ISSUE_TYPE_TO_THEME: Record<IssueTypeSegment, IssueType> = {
    FE: 'Rights issue',
    RE: 'Directed issue',
    IPO: 'IPO',
    AE: 'Non-cash issue',
}

const OFFER_SIZE_COLORS = ['#90caf9', '#64b5f6', '#42a5f5', '#1e88e5']

const getSegmentColor = (theme: Theme, segmentation: SegmentationType, segmentKey: string, index: number): string => {
    if (segmentation === 'none') {
        return theme.palette.primary.main
    }

    if (segmentation === 'issueType') {
        const col = getIssueTypeColors(theme, ISSUE_TYPE_TO_THEME[segmentKey as IssueTypeSegment])
        const bg = (col && (col.backgroundColor as string | undefined)) ?? undefined

        return bg ?? theme.palette.grey[500]
    }

    if (segmentation === 'offerSize') {
        const segmentIndex = OFFER_SIZE_SEGMENTS.indexOf(segmentKey as (typeof OFFER_SIZE_SEGMENTS)[number])
        return OFFER_SIZE_COLORS[segmentIndex >= 0 ? segmentIndex : index % OFFER_SIZE_COLORS.length]
    }

    const palette = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.info.main,
        theme.palette.error.main,
        '#7e57c2',
        '#26a69a',
    ]

    return palette[index % palette.length]
}

const getSegmentLabel = (
    segmentKey: string,
    segmentation: SegmentationType,
    t: (key: string) => string,
): string => {
    if (segmentation === 'issueType') {
        return t(`charts.issueType.${segmentKey}`)
    }

    if (segmentation === 'offerSize') {
        return t(`charts.offerSize.${segmentKey}`)
    }

    return segmentKey
}

export const AdvisorsChart = ({
    chartRows,
    segmentKeys,
    segmentation,
    onAdvisorClick,
}: {
    chartRows: ChartDisplayRow[]
    segmentKeys: string[]
    segmentation: SegmentationType
    onAdvisorClick?: (advisorId: string) => void
}) => {
    const theme = useTheme()
    const { t } = useTranslation()

    const series = useMemo(
        () =>
            segmentKeys.map(
                (segmentKey, index) =>
                    ({
                        id: segmentKey,
                        label:
                            segmentation === 'none'
                                ? t('site.table.count')
                                : getSegmentLabel(segmentKey, segmentation, t),
                        data: chartRows.map((row) => row.segmentCounts[segmentKey] ?? 0),
                        stack: segmentation === 'none' ? 'advisors' : undefined,
                        color: getSegmentColor(theme, segmentation, segmentKey, index),
                        barLabelPlacement: 'outside' as const,
                        barLabel: (item) => {
                            const value = typeof item.value === 'number' ? item.value : 0
                            return value > 0 ? value.toString() : null
                        },
                    }) as BarSeries,
            ),
        [chartRows, segmentKeys, segmentation, t, theme],
    )

    const labels = chartRows.map((row) => row.name)
    const chartHeight = Math.max(600, labels.length * 20)

    const handleItemClick = (event: MouseEvent, barItemIdentifier: { dataIndex: number }) => {
        const row = chartRows[barItemIdentifier.dataIndex]
        if (!row) return
        onAdvisorClick?.(row.id)
    }

    return (
        <BarChart
            layout='horizontal'
            height={chartHeight}
            yAxis={[
                {
                    scaleType: 'band',
                    data: labels,
                    width: 130,
                },
            ]}
            xAxis={[
                {
                    label: t('pages.statistics.advisors.issueCountAxis'),
                    domainLimit: (min, max) => ({ min, max: max.valueOf() * 1.08 }),
                },
            ]}
            series={series}
            onItemClick={handleItemClick}
            margin={{ left: 0, right: 12, top: 24, bottom: 24 }}
            slotProps={{
                bar: {
                    style: {
                        cursor: onAdvisorClick ? 'pointer' : 'default',
                    },
                },
                legend: {
                    direction: 'vertical',
                    position: { vertical: 'middle', horizontal: 'end' },
                    sx: {
                        '.MuiChartsLegend-item': {
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '.MuiChartsLegend-item button': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                        },
                        '.MuiChartsLegend-item .MuiChartsLegend-symbol': {
                            width: 12,
                            height: 12,
                        },
                    },
                },
            }}
        />
    )
}
